import { usePersonReadDataContext } from '@aibees/elephant/context';
import { usePersonUpdatesStore } from '@aibees/elephant/store/person-updates';
import { CreditUsageType } from '@aibees/shared/definition';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { LoadingButton } from '@mui/lab';
import { CircularProgress } from '@mui/material';
import { memo } from 'react';
import { CreditRuleCoin } from '../credit-rule-coin';
import {
  doesAiAnalysisHasAnyUpdates,
  isAiAnalysisUnlocked,
} from '../person-ai-analysis-info/utils';
import { useHandleEnrichPersonalityAnalysis } from '../quick-info-group/hooks/use-handle-enrich-personality-analysis';
import * as Styled from './styles';
import * as SX from './sx';
import type { UnlockAiAnalysisProps as Props } from './types';

export const UnlockAiAnalysis = memo(({ className }: Props) => {
  const hover = useBoolean(false);
  const { api } = usePersonReadDataContext();
  const unlocked = isAiAnalysisUnlocked(api?.data);
  const hasUpdates = doesAiAnalysisHasAnyUpdates(api?.data);
  const { setAnalysisUpdateTriggered } = usePersonUpdatesStore();

  const { handleAnalysis, loading, failed } =
    useHandleEnrichPersonalityAnalysis(api?.data);

  const handleUnlock = () => {
    handleAnalysis();
    hover.onFalse();
  };

  const handleUpdate = () => {
    setAnalysisUpdateTriggered(true);
    handleAnalysis(); // TODO: whenever back-end provides the update API this will be replaced!
    hover.onFalse();
  };

  const handleMouseLeave = () => {
    hover.onFalse();
  };

  const handleMouseEnter = () => {
    hover.onTrue();
  };

  if (!unlocked) return null;

  return (
    <Styled.Wrapper
      className={classnames('UnlockAiAnalysis', className)}
      sx={SX.root}
    >
      {hover.value && !loading && (
        <CreditRuleCoin
          type={CreditUsageType.CONTACT_ANALYSIS_UPDATE}
          sx={SX.creditTag}
        />
      )}
      {failed && !loading && (
        <LoadingButton
          variant="contained"
          color="primary"
          size="xsmall"
          onClick={handleUnlock}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          Retry
        </LoadingButton>
      )}
      {hasUpdates && !loading && (
        <LoadingButton
          variant="contained"
          color="primary"
          size="xsmall"
          onClick={handleUpdate}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          Update
        </LoadingButton>
      )}
      {loading && (
        <LoadingButton
          variant="contained"
          color="primary"
          size="xsmall"
          onMouseLeave={handleMouseLeave}
        >
          <CircularProgress size={18} sx={SX.loadingIcon} />
        </LoadingButton>
      )}
    </Styled.Wrapper>
  );
});

UnlockAiAnalysis.displayName = 'UnlockAiAnalysis';
